import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';

import Button from 'components/form/Button.component';
import Icon from 'components/generic/Icon.component';
import Tooltip from 'components/generic/Tooltip.component';
import { DOWNLOAD_EXCEL } from 'constants/csv.const';
import S from 'constants/string.const';
import { useTheme } from 'providers/Theme.provider';
import asArray from 'utils/arrays/asArray.util';
import { exportCSV } from 'utils/converters/csv.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import createNodeKey from 'utils/react/createNodeKey.util';
import truncateString from 'utils/strings/truncateString.util';
import C from 'utils/values/constant.util';

import CN from './Hours.module.scss';

const TOOLTIP_TIME_OFF = [
  'Total time off which is not included in working hours (ie. time off that is NOT marked as inc in working hours),',
  'that took place within salon open hours.',
].join(S.space);
const TOOLTIP_ACTUAL_DAYS = [
  'Number of days a staff member has spent in the salon, which includes time busy with appointments,',
  'time off included in working hours and empty space when the salon is open and no time off that is not ',
  'included in working hours has been booked',
].join(S.space);
const TOOLTIP_ACTUAL_MINS = [
  'Hours a staff member has spent in the salon, which includes time busy with appointments,',
  'time off included in working hours and empty space when the salon is open and no time off that is not included  ',
  'in working hours has been booked - We calculate this as salon open hours minus time off that is not included in working hours.',
].join(S.space);
const TOOLTIP_OCCUPANCY = [
  'Hours busy (time staff member was either booked with appointments or time off included in working hours)',
  'divided by hours worked (time staff member was in salon). This - matches "occupancy" in new KPI reports.',
].join(S.space);
const TOOLTIP_BUSY_MINS = [
  'Hours a staff member was in the salon and occupied, ie. not available to be booked in. ',
  'This includes both appointments and time off included in working hours.',
].join(S.space);
const TOOLTIP_BUSY_MINS_ON = 'Number of hours a staff member was busy with client appointments.';
const TOOLTIP_BUSY_MINS_OFF = 'Number of hours a staff member was busy with time off included in working hours.';
const TOOLTIP_FREE_MINS = [
  'Hours where a staff member is in the salon and available to take bookings (white space in the calendar).',
  'This is calculated as hours the business is open minus time the staff member is occupied with appointments or time off (of any type).',
].join(S.space);

const tooltipContents = C([
  { id: 1, text: TOOLTIP_TIME_OFF, label: 'Time off (non-working hours)' },
  { id: 2, text: TOOLTIP_ACTUAL_DAYS, label: 'Days worked' },
  { id: 3, text: TOOLTIP_ACTUAL_MINS, label: 'Hours worked' },
  { id: 4, text: TOOLTIP_OCCUPANCY, label: 'Utilisation' },
  { id: 5, text: TOOLTIP_BUSY_MINS, label: 'Hours busy' },
  { id: 6, text: TOOLTIP_BUSY_MINS_ON, label: 'of which booked with clients' },
  { id: 7, text: TOOLTIP_BUSY_MINS_OFF, label: 'of which blocked out for busy time' },
  { id: 8, text: TOOLTIP_FREE_MINS, label: 'Hours not busy (ie empty space in calendar)' },
]);

const fromMinutesToHoursStringTwoLineSplit = (num) => {
  if (Number.isNaN(num)) return { h: '', m: '' };

  const hours = Math.floor(num / 60);
  const minutes = Math.floor(num % 60);

  return {
    h: `${hours} hours`,
    m: `${minutes} mins`,
  };
};

const Hours = ({ salonHoursReport, startDate, endDate, stylistIDInCalendarOrder }) => {
  const { isDarkMode } = useTheme();

  const stylistGroupHours = salonHoursReport.lastRequestResponse.stylist_group || {};

  const tableHeader = (
    <tr>
      <th className={CN.firstCell}>Staff</th>
      {tooltipContents.map(({ text, label }, index) => {
        const isLast = index === tooltipContents.length - 1;
        return (
          <th key={createNodeKey({ index })}>
            <div className={CN.tableHeaderText}>
              <div className={CN.labelSpace}>{label}</div>
              <Tooltip content={text} variant={isLast ? 'quaternaryLeftToBottom' : 'quaternaryBottom'} width="max">
                <div className={CN.iconTooltip}>
                  <Icon variant="info" width="sm" />
                </div>
              </Tooltip>
            </div>
          </th>
        );
      })}
    </tr>
  );

  const tableRowsData = stylistIDInCalendarOrder
    .filter((stylist) => stylistGroupHours[stylist.id])
    .map((stylist) => {
      const stylistData = stylistGroupHours[stylist.id];

      const stylistName = truncateString({ value: stylistData.name, max: 24, suffix: '...' });
      const timeOff = fromMinutesToHoursStringTwoLineSplit(stylistData.time_off_mins || 0);
      const actualMins = fromMinutesToHoursStringTwoLineSplit(stylistData.actual_mins || 0);
      const busyMins = fromMinutesToHoursStringTwoLineSplit(stylistData.busy_mins || 0);
      const busyOnServices = fromMinutesToHoursStringTwoLineSplit(stylistData.busy_mins_on_services || 0);
      const busyOff = fromMinutesToHoursStringTwoLineSplit(stylistData.busy_mins_off || 0);
      const freeMins = fromMinutesToHoursStringTwoLineSplit(stylistData.free_mins || 0);
      const actualDays = stylistData.actual_days || 0;
      const occupancyPct = `${stylistData.occupancy_pct || 0}%`;

      return {
        stylistId: stylist.id,
        stylistName,
        timeOff,
        actualMins,
        actualDays,
        occupancyPct,
        busyMins,
        busyOnServices,
        busyOff,
        freeMins,
      };
    });

  const tableRows = asArray(
    tableRowsData.map((row) => {
      const {
        stylistId,
        stylistName,
        timeOff,
        actualMins,
        actualDays,
        occupancyPct,
        busyMins,
        busyOnServices,
        busyOff,
        freeMins,
      } = row;
      return (
        <tr key={stylistId}>
          <td className={CN.firstCell}>{stylistName}</td>
          <td>
            <div>{timeOff.h}</div>
            <div>{timeOff.m}</div>
          </td>
          <td>{actualDays}</td>
          <td>
            <div>{actualMins.h}</div>
            <div>{actualMins.m}</div>
          </td>
          <td>{occupancyPct}</td>
          <td>
            <div>{busyMins.h}</div>
            <div>{busyMins.m}</div>
          </td>
          <td>
            <div>{busyOnServices.h}</div>
            <div>{busyOnServices.m}</div>
          </td>
          <td>
            <div>{busyOff.h}</div>
            <div>{busyOff.m}</div>
          </td>
          <td>
            <div>{freeMins.h}</div>
            <div>{freeMins.m}</div>
          </td>
        </tr>
      );
    }),
  );

  const handleExportCsv = useCallback(() => {
    const headers = ['Staff', ...tooltipContents.map((tooltip) => tooltip.label)];

    const formatHM = (obj) => `${obj.h}\n${obj.m}`;

    const csvData = asArray(tableRowsData).map((item) => {
      const values = [
        item.stylistName,
        formatHM(item.timeOff),
        item.actualDays,
        formatHM(item.actualMins),
        item.occupancyPct,
        formatHM(item.busyMins),
        formatHM(item.busyOnServices),
        formatHM(item.busyOff),
        formatHM(item.freeMins),
      ];

      return Object.fromEntries(headers.map((header, i) => [header, values[i]]));
    });

    exportCSV({ data: csvData, fileName: 'Hours' });
  }, [tableRowsData]);

  const hasTableData = fromAnyToBool(tableRowsData.length);

  return (
    <div
      data-bem="Hours"
      className={cn({
        [CN.component]: true,
        [CN.darkMode]: isDarkMode,
      })}
    >
      <div className={CN.panelHeading}>
        <div className={CN.title}>Staff Working Hours</div>
        <div className={CN.buttonsContainer}>
          <Button onClick={handleExportCsv} disabled={!hasTableData} variant="primaryForce" width="small" height="xs">
            <span className={CN.buttonFont}>{DOWNLOAD_EXCEL}</span>
          </Button>
        </div>
      </div>
      <div className={CN.timeContainer}>
        <div>
          From <span className={CN.bold}>{startDate}</span>
        </div>
        <div>
          <span className={CN.gap}>to</span> <span className={CN.bold}>{endDate}</span>
        </div>
      </div>
      <div className={CN.scrollContainer}>
        <table className={CN.table} id="Hours">
          <thead>{tableHeader}</thead>
          <tbody>{tableRows}</tbody>
        </table>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  salonHoursReport: state?.reports?.salonHoursReport,
  stylistIDInCalendarOrder: state?.reports?.stylistIDInCalendarOrder,
});

const HoursHoc = connect(mapStateToProps)(Hours);

export default HoursHoc;
